import React, { useState } from 'react';
import { Button, Modal, DatePicker, message, Form } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

/**
 * Componente ModalDownloadCSV
 * 
 * Componente que renderiza un botón que al hacer clic abre un modal con un selector 
 * de mes y año. Al seleccionar el período y confirmar,
 * realiza una petición GET al backend con las fechas de inicio y fin del mes seleccionado,
 * y descarga automáticamente el archivo CSV devuelto por el servidor.
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.endpoint='/api/download-csv'] - URL del endpoint del backend para descargar el CSV
 * @param {string} [props.buttonText='Descargar CSV'] - Texto a mostrar en el botón
 * @param {string} [props.title='Seleccionar Mes'] - Título del modal
 * @param {Object} [props.params={}] - Parámetros adicionales a enviar en la petición GET (se combinan con las fechas)
 * 
 * 
 * @returns {JSX.Element} Botón con modal para descarga de CSV
 */
const ModalDownloadCSV = ({ 
    endpoint = '/api/download-csv', 
    buttonText = 'Descargar CSV', 
    title = 'Seleccionar Mes',
    params = {}
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    /**
     * Maneja la descarga del archivo CSV
     * Envía las fechas calculadas del mes seleccionado al backend y descarga el archivo resultante
     * 
     * @param {Object} values - Valores del formulario
     * @param {moment} values.mes - Fecha del mes seleccionado (moment object)
     */
    const handleDownload = async (values) => {
        if (!values.mes) {
            message.error('Por favor selecciona un mes');
            return;
        }

        // Calcular primer y último día del mes seleccionado
        const fechaInicio = values.mes.clone().startOf('month');
        const fechaFin = values.mes.clone().endOf('month');
        
        setLoading(true);
        try {
            // Construir URL con parámetros
            const url = new URL(axios.defaults.baseURL + endpoint);
            url.searchParams.set('Authorization', sessionStorage.getItem('token'))
            url.searchParams.append('createdAt', JSON.stringify([fechaInicio.toISOString(), fechaFin.toISOString()]));
            url.searchParams.append('csv', 'true');
            
            // Agregar parámetros adicionales
            Object.keys(params).forEach(key => {
                url.searchParams.append(key, params[key]);
            });

            window.open(url, '_blank');

            message.success('CSV descargado exitosamente');
            handleCancel();
        } catch (error) {
            console.error('Error al descargar CSV:', error);
            message.error(error.message || 'Error al descargar el archivo CSV');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={showModal}
            >
                {buttonText}
            </Button>

            <Modal
                title={title}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleDownload}
                >
                    <Form.Item
                        label="Seleccionar Mes y Año"
                        name="mes"
                        rules={[{ required: true, message: 'Por favor selecciona un mes' }]}
                    >
                        <DatePicker 
                            picker="month"
                            style={{ width: '100%' }} 
                            format="MMMM YYYY"
                            placeholder="Seleccionar mes"
                            disabledDate={(current) => {
                                // Deshabilitar fechas después del mes actual
                                if (current && current > moment().endOf('month')) {
                                    return true;
                                }
                                // Deshabilitar fechas antes de enero 2020
                                if (current && current < moment('2020-01-01').startOf('month')) {
                                    return true;
                                }
                                return false;
                            }}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={loading}
                            block
                            icon={<DownloadOutlined />}
                        >
                            Descargar CSV
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ModalDownloadCSV;
